import { IPersonName } from './../../../models/registration/personName';
import * as fromRegistration from './../selectors/registration.selectors';
import * as fromRegistrant from './../selectors/registrants.selectors';
import * as fromInvestor from './../selectors/investor.selectors';
import * as fromBank from './../selectors/banks.selectors';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { IAppState } from 'src/app/store/state/app.state';
import { Observable } from 'rxjs';
import { IRegisterUser } from 'src/app/models/registration/register';
import { IInvestorDetails } from 'src/app/models/registration/investorDetails';
import { IPersonalDetails } from 'src/app/models/registration/personalDetails';
import { ITaxDetails } from 'src/app/models/registration/taxDetails';
import { ISourceOfWealth } from 'src/app/models/registration/sourceOfWealth';
import { IRegistrantDetails } from 'src/app/models/registration/registrantDetails';
import { IVerification } from 'src/app/models/registration/verification';
import { IBankDetail } from 'src/app/models/registration/bankDetails';
import { map, tap } from 'rxjs/operators';
import * as registrationActions from '../actions/registration.actions';
import * as systemActions from '../../system/actions/system.actions';
import { IRegistrationState, IRegistrationStatusState } from '../../state/registration.state';

@Injectable()
export class RegistrationFacade {
  investorEntityId$: Observable<number>;
  legalEntityType$: Observable<number>;
  status$: Observable<number>;
  state$: Observable<IRegistrationStatusState>;
  failtext$: Observable<string>;

  constructor(private store: Store<IAppState>) {
    this.investorEntityId$ = store.pipe(
      select(fromInvestor.selectInvestorEntityId)
    );
    this.legalEntityType$ = store.pipe(
      select(fromInvestor.selectRegistrantInvestorLegalEntityType)
    );
    this.status$ = store.pipe(
      select(fromRegistration.selectRegistrationStatus)
    );
    this.failtext$ = store.pipe(
      select(fromRegistration.selectRegistrationFailText)
    );
    this.state$ = store.pipe(
      select(fromRegistration.selectRegistrationStatusState)
    );
  }

  register(user: IRegisterUser) {
    this.store.dispatch(new systemActions.ClearSystemState());
    this.store.dispatch(new registrationActions.Register(user));
  }

  loadRegistrationSteps(): Observable<IRegistrationState> {
    return this.store.pipe(
      select(fromRegistration.getRegistrationState),
      tap((result: IRegistrationState) => {
        if (!result.investor.details) {
          this.store.dispatch(new registrationActions.RegistrationLoadSteps());
        }
      })
    );
  }

  getRegistrants(): Observable<IRegistrantDetails[]> {
    return this.store.pipe(select(fromRegistrant.selectRegistrants));
  }

  getRegistrantIds(): Observable<Number[] | string[]> {
    return this.store.pipe(select(fromRegistrant.getRegistrantIds));
  }

  getRegistrantsCount(): Observable<number> {
    return this.getRegistrantIds().pipe(map(i => i.length));
  }

  getRegistrantById(id: number): Observable<IRegistrantDetails> {
    return this.store.pipe(select(fromRegistrant.selectRegistrantById(id)));
  }

  getFirstRegistrantName(): Observable<IPersonName> {
    return this.store.pipe(select(fromRegistrant.selectFirstRegistrantName));
  }

  getInvestorDetails(): Observable<IInvestorDetails> {
    return this.store.pipe(select(fromInvestor.selectInvestorDetails));
  }

  getInvestorReinvestmentOption(): Observable<string> {
    return this.store.pipe(
      select(fromInvestor.selectInvestorReinvestmentOption)
    );
  }

  getBankDetails(): Observable<IBankDetail> {
    return this.store.pipe(select(fromBank.selectBankDetails));
  }

  saveRegistration() {
    this.store.dispatch(new registrationActions.RegistrationSave());
  }

  saveFinalRegistration() {
    this.store.dispatch(new registrationActions.RegistrationFinalSave());
  }

  updateRegistrationStatus(status: number) {
    this.store.dispatch(
      new registrationActions.RegistrationUpdateStatus(status)
    );
  }

  updateInvestorDetails(val: IInvestorDetails) {
    this.store.dispatch(
      new registrationActions.RegistrationUpdateInvestorDetails(val)
    );
  }

  updateReinvestmentOption(val: string) {
    this.store.dispatch(
      new registrationActions.RegistrationUpdateReinvestmentOption(val)
    );
  }

  updatePersonalDetails(entityId: number, val: IPersonalDetails) {
    this.store.dispatch(
      new registrationActions.RegistrationUpdatePersonalDetails(val, entityId)
    );
  }

  updateTaxDetails(entityId: number, val: ITaxDetails) {
    this.store.dispatch(
      new registrationActions.RegistrationUpdateTaxDetails(val, entityId)
    );
  }

  updateSourceOfWealth(entityId: number, val: ISourceOfWealth) {
    this.store.dispatch(
      new registrationActions.RegistrationUpdateSourceOfWealth(val, entityId)
    );
  }


  verifyMember() {
    this.store.dispatch(
      new registrationActions.RegistrationVerifyMember()
    );
  }

  verifyMemberSuccess() {
    this.store.dispatch(
      new registrationActions.RegistrationVerifyMemberSuccess()
    );
  }

  public verifyMemberFail() {
    this.store.dispatch(
      new registrationActions.RegistrationVerifyMemberFail()
    );
  }


  updateVerificationDetails(entityId: number, val: IVerification) {
    this.store.dispatch(
      new registrationActions.RegistrationUpdateVerificationDetails(
        val,
        entityId
      )
    );
  }

  updateBankDetails(val: IBankDetail) {
    this.store.dispatch(
      new registrationActions.RegistrationUpdateBankDetails(val)
    );
  }

  addRegistrant(val: IRegistrantDetails) {
    this.store.dispatch(new registrationActions.RegistrationAddRegistrant(val));
  }
}
